import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { VideoPhone } from "@/components/public/video-phone"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const INCLUDED = [
  {
    icon: "film",
    title: "Up to 30 videos per month",
    description: "Professional property videos created from your listing photos. Every template, every style.",
  },
  {
    icon: "share",
    title: "Unlimited social media posts",
    description: "We publish across Instagram, Facebook, TikTok, and YouTube. As many posts as you need, every month.",
  },
  {
    icon: "clock",
    title: "Zero time from your team",
    description: "We handle everything: listing import, video creation, captions, scheduling, and publishing. You do nothing.",
  },
  {
    icon: "user",
    title: "Dedicated account manager",
    description: "A real person you can reach any time. Request changes, ask questions, adjust your strategy.",
  },
  {
    icon: "mic",
    title: "Professional voiceover and music",
    description: "Every video gets a polished voiceover and background music. No robotic voices, no stock silence.",
  },
  {
    icon: "palette",
    title: "Your agency branding",
    description: "Your logo, your colours, your identity. Every video looks like it came from your own production team.",
  },
]

const ICON_PATHS: Record<string, string> = {
  film: "M3.375 3C2.339 3 1.5 3.84 1.5 4.875v11.25C1.5 17.16 2.34 18 3.375 18h17.25c1.035 0 1.875-.84 1.875-1.875V4.875C22.5 3.839 21.66 3 20.625 3H3.375ZM12 9.75a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H12Zm-.75-2.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM6 12.75a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5H6Zm-.75 3.75a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75ZM6 6.75a.75.75 0 0 0-.75.75v3c0 .414.336.75.75.75h3a.75.75 0 0 0 .75-.75v-3A.75.75 0 0 0 9 6.75H6Z",
  share: "M15.75 4.5a3 3 0 1 1 .825 2.066l-8.421 4.679a3.002 3.002 0 0 1 0 1.51l8.421 4.679a3 3 0 1 1-.729 1.31l-8.421-4.678a3 3 0 1 1 0-4.132l8.421-4.679A3 3 0 0 1 15.75 4.5Z",
  clock: "M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z",
  user: "M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z",
  mic: "M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5ZM6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709V21h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-1.541A6.751 6.751 0 0 1 5.25 12.75v-1.5a.75.75 0 0 1 .75-.75Z",
  palette: "M5.566 4.657A4.505 4.505 0 0 1 6.75 4.5h10.5c.41 0 .806.055 1.183.157A3 3 0 0 0 15.75 3h-7.5a3 3 0 0 0-2.684 1.657ZM2.25 12a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3v-6ZM5.25 7.5c-.41 0-.806.055-1.184.157A3 3 0 0 1 6.75 6h10.5a3 3 0 0 1 2.683 1.657A4.505 4.505 0 0 0 18.75 7.5H5.25Z",
}

const SAMPLE_VIDEOS = [
  {
    src: "https://dudpw00jtfdjq.cloudfront.net/videos/39/8gml0euqb9uW/puAhVLs74w79.mp4",
    property: "Westbrook, Bromham",
    price: "£1,195,000",
  },
  {
    src: "https://dudpw00jtfdjq.cloudfront.net/videos/28/ukwILmwacdX7/KNRBNsX289E7.mp4",
    property: "Ocean Key, Feock",
    price: "£1,695,000",
  },
  {
    src: "https://dudpw00jtfdjq.cloudfront.net/videos/16/SpXGy1x0R0p6/zhegqQwTywkh.mp4",
    property: "Leighton Road, Cheltenham",
    price: "£525,000",
  },
]

const FAQ = [
  {
    q: "What exactly do you handle?",
    a: "Everything. We import your listings, create the videos, write captions, schedule posts, and publish across all your social channels. You don\u2019t lift a finger.",
  },
  {
    q: "How many videos do I get?",
    a: "Up to 30 professional property videos every month. That\u2019s enough to cover every new listing and keep your social feeds active.",
  },
  {
    q: "What about social media posts?",
    a: "Unlimited. We publish as many posts as you need across Instagram, Facebook, TikTok, and YouTube. Reels, stories, feed posts: all included.",
  },
  {
    q: "Do I need to spend any time on this?",
    a: "No. That\u2019s the whole point. We handle creation, publishing, and management. If you want to request changes or adjust direction, just reach out to your account manager.",
  },
  {
    q: "Can I request changes to videos?",
    a: "Of course. Your dedicated account manager is always available. Want a different style, updated photos, or a specific caption? Just ask.",
  },
  {
    q: "Is there a contract?",
    a: "No long-term commitment. Month-to-month billing, cancel any time. We\u2019re confident you\u2019ll stay because the results speak for themselves.",
  },
  {
    q: "What does each video include?",
    a: "Professional voiceover, background music, your agency branding, auto-generated captions, and publishing to Instagram, Facebook, TikTok, and YouTube.",
  },
  {
    q: "How quickly do videos go live?",
    a: "Most videos are created and published within 24 hours of a listing going live. We monitor your listings so you don\u2019t have to tell us.",
  },
]

/* ------------------------------------------------------------------ */
/* Reusable check icon                                                 */
/* ------------------------------------------------------------------ */

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function Pricing({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          })}
        </script>
      </Head>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="px-6 pt-16 md:pt-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            {/* Left — copy */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-red">
                Fully managed
              </p>
              <h1 className="mt-4 font-display text-[2.5rem] font-bold leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-[3.25rem]">
                We handle your<br />
                property marketing<span className="text-red">.</span>
                <br />
                You close deals<span className="text-red">.</span>
              </h1>
              <p className="mt-4 max-w-md text-base leading-relaxed text-slate md:text-lg">
                Professional property videos, unlimited social media posts, and
                zero hours from your team. One plan, one price, total peace of mind.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  href="/sign-up?plan=managed"
                  className="inline-flex items-center justify-center rounded-xl bg-red px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-deep-red"
                >
                  Get started
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-xl border border-charcoal/15 px-8 py-3.5 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal/25 hover:bg-charcoal/5"
                >
                  Book a walkthrough
                </Link>
              </div>
            </div>

            {/* Right — video sample */}
            <div className="flex justify-center lg:justify-end">
              <div className="scale-[0.85] sm:scale-90 lg:scale-100">
                <VideoPhone src={SAMPLE_VIDEOS[0].src} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Single plan card ─────────────────────────────────────── */}
      <section className="px-6 pb-16 pt-12 md:pb-20 md:pt-14">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <div className="relative overflow-hidden rounded-3xl bg-charcoal shadow-2xl">
              {/* Top accent bar */}
              <div className="h-1 w-full bg-gradient-to-r from-red via-red/60 to-transparent" />

              <div className="p-8 md:p-12">
                {/* Price centred at the top */}
                <div className="text-center">
                  <span className="rounded-full bg-red/15 px-3.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Fully managed
                  </span>
                  <div className="mt-6 flex items-baseline justify-center gap-1.5">
                    <span className="font-display text-6xl font-bold tracking-tight text-cream md:text-7xl">
                      £499
                    </span>
                    <span className="text-lg text-cream/40">/mo</span>
                  </div>
                  <p className="mt-2 text-sm text-cream/50">
                    Month-to-month. No contract. Cancel any time.
                  </p>
                </div>

                {/* Divider */}
                <div className="mx-auto my-8 h-px w-full max-w-md bg-cream/10" />

                {/* Feature grid */}
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  {[
                    { text: "Up to 30 property videos per month", highlight: true },
                    { text: "Unlimited social media posts", highlight: true },
                    { text: "Zero time required from your team", highlight: true },
                    { text: "Dedicated account manager", highlight: false },
                    { text: "Professional voiceover and music", highlight: false },
                    { text: "Your agency branding on every video", highlight: false },
                    { text: "Publishing to Instagram, Facebook, TikTok, YouTube", highlight: false },
                    { text: "Request changes any time", highlight: false },
                  ].map((item) => (
                    <div key={item.text} className="flex items-start gap-3">
                      <CheckIcon className="mt-0.5 size-5 shrink-0 text-red" />
                      <span className={`text-[15px] leading-snug ${item.highlight ? "font-medium text-cream" : "text-cream/65"}`}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-10 text-center">
                  <Link
                    href="/sign-up?plan=managed"
                    className="inline-flex items-center justify-center rounded-xl bg-red px-10 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-deep-red hover:shadow-lg hover:shadow-red/20"
                  >
                    Start your first month
                  </Link>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* ROI nudge */}
          <ScrollReveal className="mt-6">
            <div className="rounded-xl border border-stone bg-white px-6 py-4 text-center">
              <p className="text-sm text-charcoal">
                That's less than <span className="font-semibold text-red">£17 per video</span>.
                A single property video from a freelancer costs £150+.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── ROI comparison ───────────────────────────────────────── */}
      <section className="border-t border-stone px-6 py-24 md:py-28">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-red">
              The real cost of doing it yourself
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal md:text-4xl">
              Your time is worth more<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-base text-slate">
              Most agencies spend 8+ hours a week on social content. That's time
              your negotiators could spend closing deals.
            </p>
          </div>

          <ScrollReveal stagger className="mt-14 grid gap-5 md:grid-cols-3">
            {/* DIY */}
            <div className="rounded-2xl border border-stone bg-white p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-warm-grey">Do it yourself</p>
              <p className="mt-4 font-display text-3xl font-bold text-charcoal">8+ hrs</p>
              <p className="mt-1 text-sm text-warm-grey">per week</p>
              <div className="my-5 h-px bg-stone" />
              <ul className="space-y-2.5">
                {["Film or source footage", "Edit in video software", "Write captions", "Schedule and publish", "Manage multiple platforms"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0 text-warm-grey/60">
                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Freelancer */}
            <div className="rounded-2xl border border-stone bg-white p-7">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-warm-grey">Hire a freelancer</p>
              <p className="mt-4 font-display text-3xl font-bold text-charcoal">£150+</p>
              <p className="mt-1 text-sm text-warm-grey">per video</p>
              <div className="my-5 h-px bg-stone" />
              <ul className="space-y-2.5">
                {["Brief for every listing", "Review rounds", "Inconsistent quality", "No social management", "30 videos = £4,500+/mo"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0 text-warm-grey/60">
                      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* QuiteRight */}
            <div className="relative rounded-2xl bg-charcoal p-7 shadow-xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-red px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-md">
                  Best value
                </span>
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-red">QuiteRight</p>
              <p className="mt-4 font-display text-3xl font-bold text-cream">£499</p>
              <p className="mt-1 text-sm text-cream/40">per month, everything included</p>
              <div className="my-5 h-px bg-cream/10" />
              <ul className="space-y-2.5">
                {["Zero hours from your team", "Up to 30 videos", "Unlimited social posts", "Consistent brand quality", "Dedicated account manager"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-cream/70">
                    <CheckIcon className="size-4 shrink-0 text-red" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────── */}
      <section className="border-t border-stone px-6 py-24 md:py-28">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-red">
              Three steps
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal md:text-4xl">
              How it works<span className="text-red">.</span>
            </h2>
          </div>

          <ScrollReveal stagger className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              { step: "01", title: "Sign up", description: "Create your account and connect your listings. Takes two minutes." },
              { step: "02", title: "We create", description: "Our team produces your property videos and social content. You do nothing." },
              { step: "03", title: "You grow", description: "Videos go live across your social channels. More views, more enquiries, more deals." },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <span className="font-display text-4xl font-bold text-charcoal/10">{item.step}</span>
                <h3 className="mt-2 font-display text-lg font-bold text-charcoal">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── What's included detail ───────────────────────────────── */}
      <section className="border-t border-stone bg-cream/30 px-6 py-24 md:py-28">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-red">
              What you get
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal md:text-4xl">
              Everything, handled<span className="text-red">.</span>
            </h2>
          </div>

          <ScrollReveal stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDED.map((item) => (
              <div key={item.title} className="rounded-2xl border border-stone bg-white p-7 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex size-10 items-center justify-center rounded-xl bg-red/5">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="size-5 text-red">
                    <path fillRule="evenodd" d={ICON_PATHS[item.icon]} clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="mt-4 font-display text-[15px] font-bold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {item.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── Video showcase ───────────────────────────────────────── */}
      <section className="border-t border-stone bg-charcoal px-6 py-24 md:py-28">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-red">
              Real examples
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-cream md:text-4xl">
              This is what you get<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-md text-base text-warm-grey">
              Every video below was created from listing photos.
              No filming. No editing. We handle it all.
            </p>
          </div>

          <ScrollReveal stagger className="mt-14 grid gap-10 sm:grid-cols-3 sm:gap-6">
            {SAMPLE_VIDEOS.map((video) => (
              <div key={video.src} className="text-center">
                <div className="mx-auto scale-[0.82] sm:scale-[0.78] md:scale-[0.82] lg:scale-90">
                  <VideoPhone src={video.src} />
                </div>
                <p className="mt-5 font-display text-sm font-semibold text-cream">
                  {video.property}
                </p>
                <p className="mt-0.5 text-xs text-warm-grey">{video.price}</p>
              </div>
            ))}
          </ScrollReveal>

          <div className="mt-14 text-center">
            <Link
              href="/examples"
              className="inline-flex items-center gap-2 text-sm font-medium text-cream transition-colors hover:text-red"
            >
              See more examples
              <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638l-3.96-3.96a.75.75 0 1 1 1.06-1.06l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06l3.96-3.96H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <section className="px-6 py-24 md:py-28">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-center font-display text-3xl font-bold tracking-tight text-charcoal">
            Questions<span className="text-red">.</span>
          </h2>

          <div className="mt-12">
            {FAQ.map((item, i) => (
              <details
                key={item.q}
                className={`group ${i > 0 ? "border-t border-stone/80" : ""}`}
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 py-5 text-[15px] font-medium text-charcoal hover:text-red">
                  {item.q}
                  <svg
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    className="size-4 shrink-0 text-warm-grey transition-transform duration-200 group-open:rotate-180 group-open:text-red"
                  >
                    <path fillRule="evenodd" d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className="pb-5 pr-10 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="border-t border-stone bg-charcoal px-6 py-24 md:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-cream md:text-4xl">
            Stop spending hours on content<span className="text-red">.</span>
            <br />
            Start closing more deals<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base text-warm-grey">
            £499/month. Everything handled. Your first videos go live within 48 hours.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="/sign-up?plan=managed"
              className="inline-flex items-center rounded-xl bg-red px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-deep-red"
            >
              Get started
            </Link>
            <Link
              href="/demo"
              className="inline-flex items-center rounded-xl border border-white/15 px-8 py-3.5 text-sm font-semibold text-cream transition-colors hover:border-white/25 hover:bg-white/5"
            >
              Book a walkthrough
            </Link>
          </div>
          <p className="mt-6 text-xs text-cream/30">
            No contract. No setup fee. Cancel any time.
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
